import { useCallback, useState } from 'react';
import type { LecturaFichaObra } from '@matias-asset-os/contracts';
import { t, type Idioma } from '@matias-asset-os/i18n';
import { api, ErrorApi } from '../api.js';

/**
 * EL TACO DE FICHAS NO SE PIERDE AL DAR DE ALTA UNA.
 *
 * Las fichas leidas vivian dentro de la pantalla que las enseña. Entrar a
 * repasar una la desmontaba —el alta es otra pantalla—, y al volver la lista
 * aparecia vacia: se subian tres fotos, se daba de alta una, y las otras dos
 * habia que volver a fotografiarlas y volver a leerlas, pagando otra vez la
 * lectura. Justo el trabajo que este modulo viene a quitar.
 *
 * Ahora el taco vive en el modulo, por encima de las pantallas: se abre una, se
 * da de alta, se vuelve y las demas siguen ahi esperando. La que se da de alta
 * se quita sola, para que nadie la repase dos veces.
 *
 * Dura lo que dura la sesion del modulo, no mas: son fotos del dia con datos de
 * gente, y no tienen por que quedarse guardadas en el navegador.
 */

export type EstadoFicha =
  | { fase: 'leyendo' }
  | { fase: 'leida'; lectura: LecturaFichaObra }
  | { fase: 'fallo'; motivo: string };

export interface Ficha {
  id: string;
  fichero: File;
  /** La miniatura, para saber que papel es cada tarjeta sin abrirla. */
  vista: string;
  estado: EstadoFicha;
}

export interface FichasDeCampo {
  fichas: Ficha[];
  /** Cuantas se estan leyendo ahora mismo. */
  leyendo: number;
  anadir: (elegidos: FileList | null) => void;
  quitar: (id: string) => void;
}

export function useFichasDeCampo(idioma: Idioma): FichasDeCampo {
  const [fichas, setFichas] = useState<Ficha[]>([]);

  const anadir = useCallback(
    (elegidos: FileList | null) => {
      const nuevas: Ficha[] = [...(elegidos ?? [])]
        .filter((f) => f.type.startsWith('image/'))
        .map((fichero) => ({
          id: crypto.randomUUID(),
          fichero,
          vista: URL.createObjectURL(fichero),
          estado: { fase: 'leyendo' } as EstadoFicha,
        }));
      if (nuevas.length === 0) return;
      setFichas((antes) => [...antes, ...nuevas]);

      // Una a una y no todas a la vez: son peticiones caras, y diez fotos en
      // paralelo es la forma mas rapida de que el proveedor conteste 429.
      void (async () => {
        for (const ficha of nuevas) {
          let estado: EstadoFicha;
          try {
            const { lectura } = await api.leerFichaDeObra(ficha.fichero);
            estado = { fase: 'leida', lectura };
          } catch (e) {
            estado = {
              fase: 'fallo',
              motivo:
                e instanceof ErrorApi && e.message ? e.message : t(idioma, 'obras.fichaNoSeLeyo'),
            };
          }
          setFichas((antes) => antes.map((f) => (f.id === ficha.id ? { ...f, estado } : f)));
        }
      })();
    },
    [idioma],
  );

  const quitar = useCallback((id: string) => {
    setFichas((antes) => {
      const fuera = antes.find((f) => f.id === id);
      if (fuera) URL.revokeObjectURL(fuera.vista);
      return antes.filter((f) => f.id !== id);
    });
  }, []);

  return {
    fichas,
    leyendo: fichas.filter((f) => f.estado.fase === 'leyendo').length,
    anadir,
    quitar,
  };
}
